import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as auth from "./AuthRedux";
import { RootState } from "setup/redux/RootReducer";
import { ConnectedProps, connect, shallowEqual, useDispatch, useSelector } from "react-redux";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { IMaintenanceService } from "services/MaintenanceService";
import { setCPLMaintenanceDates } from "app/system/maintenance/MaintenanceSlice";
import LayoutSplashScreen from "_metronic/layout/core/LayoutSplashScreen";
import { IAuthState } from "./AuthRedux";
import { IClientPortalCPLSettingsService } from "services/ClientPortalCPLSettingsService";
import { ISessionService } from "services/SessionService";
import { setGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";
import { getActiveLoginType } from "services/loginTypeStorage";
import { isEmbeddedApp } from "utils/embeddedApp";
import { LoginType } from "global/enums";

const mapState = (state: RootState) => ({ auth: state.auth });
const connector = connect(mapState, auth.actions);
type PropsFromRedux = ConnectedProps<typeof connector>;

const AuthInit: React.FC<PropsWithChildren<PropsFromRedux>> = (props) => {
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const authState = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getGlobalSettings = async () => {
    const clientPortalCPLSettingsService = container.get<IClientPortalCPLSettingsService>(
      SERVICE_TYPES.ClientPortalCPLSettingsService
    );
    const globalSettings = await clientPortalCPLSettingsService.getGlobalClientPortalSettings();
    dispatch(setGlobalSettings(globalSettings));
  };

  useEffect(() => {
    const requestUser = () => {
      if (!didRequest.current) {
        props.requestUser({
          errorHandler: (error) => {
            console.error(error);
            if (!didRequest.current) {
              if (getActiveLoginType() !== LoginType.Normal) {
                navigate("/auth");
                setShowSplashScreen(false);
              } else {
                props.logout({ successHandler: () => setShowSplashScreen(false) });
              }
            }
          },
          successHandler: async () => {
            await getGlobalSettings();
            setShowSplashScreen(false);
          },
        });
      }

      return () => (didRequest.current = true);
    };

    const initAuth = async () => {
      const maintenanceService = container.get<IMaintenanceService>(SERVICE_TYPES.MaintenanceService);
      const maintenanceDates = await maintenanceService.getMaintenanceDates();
      const convertedDates = maintenanceService.convertMaintenanceDates(maintenanceDates);
      dispatch(setCPLMaintenanceDates(convertedDates));

      const isDownForMaintenance = maintenanceService.checkIfDownForMaintenance(convertedDates);

      if (isDownForMaintenance) {
        setShowSplashScreen(false);
        return;
      }

      if (authState.token) {
        requestUser();
        return;
      }

      // CPL Mobile APP seeds - US-27(#9391): embed contract: the mobile cplsessiontoken/… into localStorage at
      // document start; and will catch here when it is acessed from a mobile app for portal
      const sessionService = container.get<ISessionService>(SERVICE_TYPES.SessionService);
      const recoveredToken = isEmbeddedApp() ? await sessionService.getCPLSessionToken().catch(() => "") : "";

      if (recoveredToken) {
        dispatch(auth.actions.setToken(recoveredToken));
        requestUser();
      } else if (getActiveLoginType() !== LoginType.Normal) {
        navigate("/auth");
        setShowSplashScreen(false);
      } else {
        props.logout();
        setShowSplashScreen(false);
      }
    };

    if (!authState.externalAccess) {
      initAuth();
    } else {
      setShowSplashScreen(false);
    }

    // Note: Effect should run only once, recheck dependencies if effect is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{props.children}</>;
};

export default connector(AuthInit);
